<template>
  <div class="bg-primary">
    <!-- Header -->
    <div class="header bg-gradient-blue py-7 py-lg-6 pt-lg-5">
      <b-container class="container">
        <div class="header-body text-center mb-5">
          <b-row class="justify-content-center">
            <b-col xl="5" lg="6" md="8" class="px-5">
              <h1 class="text-white">Create an account</h1>
              <p class="text-lead text-white">Please use the form below to register.</p>
            </b-col>
          </b-row>
        </div>
      </b-container>
      <div class="separator separator-bottom separator-skew zindex-100">
        <svg
          x="0"
          y="0"
          viewBox="0 0 2560 100"
          preserveAspectRatio="none"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
        >
          <polygon class="fill-primary" points="2560 0 2560 100 0 100" />
        </svg>
      </div>
    </div>
    <!-- Page content -->
    <b-container class="mt--7 pb-5">
      <!-- Table -->
      <b-row class="justify-content-center">
        <b-col lg="5" md="7">
          <b-card no-body class="bg-secondary border-0 mb-0">
            <b-card-body class="px-lg-5 py-lg-5">
              <div class="text-center text-muted mb-4">
                <small>Please provide your information.</small>
              </div>
              <validation-observer v-slot="{ handleSubmit }" ref="formValidator">
                <b-form role="form" @submit.prevent="handleSubmit(register)">
                  <base-input
                    alternative
                    class="mb-3"
                    prepend-icon="ni ni-circle-08"
                    placeholder="Name"
                    name="Name"
                    :rules="{ required: false }"
                    :debounce="500"
                    v-model="model.name"
                  ></base-input>

                  <base-input
                    alternative
                    class="mb-3"
                    prepend-icon="ni ni-email-83"
                    placeholder="Email"
                    name="Email"
                    :rules="{ required: true, email: true }"
                    :debounce="500"
                    v-model="model.email"
                  ></base-input>

                  <base-input
                    alternative
                    class="mb-3"
                    prepend-icon="ni ni-lock-circle-open"
                    placeholder="Password"
                    type="password"
                    name="Password"
                    :rules="{ required: true, min: 8 }"
                    :debounce="500"
                    v-model="model.password"
                  ></base-input>
                  <div class="text-muted">
                    <small v-if="passwordStrength">
                      Password strength:
                      <span
                        :class="passwordStrength.class"
                        class="font-weight-700"
                      >{{ passwordStrength.value }}</span>
                    </small>
                    <small v-else>&nbsp;</small>
                  </div>
                  <b-row class="mt-4">
                    <b-col cols="12">
                      <base-input
                        :rules="{ required: { allowFalse: false } }"
                        name="Accept Terms Of Service"
                        Policy
                      >
                        <b-form-checkbox v-model="model.agree">
                          <span class="text-muted">
                            I accept the
                            <a
                              target="_blank"
                              href="https://adminhero.co/terms-of-service"
                            >Terms of Service</a>
                          </span>
                        </b-form-checkbox>
                      </base-input>
                    </b-col>
                  </b-row>
                  <div class="text-center">
                    <base-button native-type="submit" type="primary" class="my-4 btn-block">
                      <span v-if="!registering">
                        Register now
                        <i class="fa fa-arrow-right ml-2"></i>
                      </span>
                      <span v-else>
                        Please wait&hellip;
                        <i class="fa fa-spin fa-spinner ml-2"></i>
                      </span>
                    </base-button>
                  </div>

                  <base-alert
                    v-if="errorMessage"
                    :dismissible="false"
                    type="default"
                    icon="fas fa-exclamation-circle"
                  >Error: {{ errorMessage }}</base-alert>
                </b-form>
              </validation-observer>
            </b-card-body>
          </b-card>
          <b-row class="mt-3">
            <b-col cols="6" offset="6" class="text-right">
              <router-link to="/login" class="text-light">
                <small>
                  Already have an account? Log in
                  <i class="fa fa-sign-in-alt"></i>
                </small>
              </router-link>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>
<script>
import { BaseAlert } from "@/components";
import { get } from "dot-prop";
import passwordStrength from "check-password-strength";
import { parseFullName } from "parse-full-name";

export default {
  name: "register",
  components: {
    BaseAlert,
  },
  data() {
    return {
      registering: false,
      model: {
        name: "",
        email: "",
        password: "",
        agree: false,
      },
    };
  },
  computed: {
    passwordStrength() {
      if (this.model.password.length < 1) {
        return null;
      } else {
        const strength = passwordStrength(this.model.password);

        let result = strength;
        if (strength.id === 0) {
          result.class = "text-danger";
        } else if (strength.id === 1) {
          result.class = "text-warning";
        } else if (strength.id === 2) {
          result.class = "text-success";
        }

        return result;
      }
    },
  },
  methods: {
    register() {
      this.registering = true;
      const parsedName = parseFullName(this.model.name);
      // this will be called only after form is valid. You can do an api call here to register users
      this.$auth.register({
        data: {
          emailAddress: this.model.email,
          password: this.model.password,
          firstName: parsedName.first,
          lastName: parsedName.last,
        },
        redirect: false,
        success: (response) => {
          // now log the user in
          this.$auth.login({
            data: {
              grantType: "password",
              emailAddress: this.model.email,
              password: this.model.password,
            },
            success: () => {
              this.registering = false;
            },
            redirect: "/?welcome=true",
          });
        },
        error: (error) => {
          this.registering = false;
          this.errorMessage = `Error registering: ${error.response.statusText}`;
          if (get(error, "response.data.message")) {
            this.errorMessage = error.response.data.message;
          }
        },
        autoLogin: false,
        rememberMe: false,
      });
    },
  },
};
</script>
<style></style>
